import {
  FluidComponentDefinition,
  FluidFormElementConfig,
  FluidFormElementType,
  FluidIconKey,
  FluidRowData,
  FluidSize,
  FluidTableHeader,
  FluidTheme,
  FluidToggleConfig,
  FluidToggleType,
  FluidUpdateStrategy,
} from '@lmig/fluid-core';
import { generateUniqueId } from '@lmig/fluid-core/lib/helpers/string.helpers';
import { h } from '@stencil/core';

import { PopoverTooltip } from '../../../common/fluid-common-popover.components';
import { FluidComponentFromDefinition } from '../../../common/fluid-component-rendering.components';
import { ID } from '../../../constants/constants';
import { EventUtils } from '../../../utils/events/event-utils';
import { Utils } from '../../../utils/utils';
import { FluidTable } from '../fluid-table';
import {
  cellData,
  getDataAtPath,
  getTruncatedValue,
} from '../internal/fluid-table-data-management.utils';
import { addElementReference } from '../internal/fluid-table-form.utils';
import {
  convertNonBooleanIndicatorToBoolean,
  isValidValue,
} from '../internal/fluid-table-select.utils';
import { handleCellClick } from '../internal/fluid-table-structure.utils';
import { getCellAlignment } from '../internal/fluid-table-templating.utils';

// ================================================================ //
// -- Functional Interfaces

export interface TableSelectCellProps {
  allowTabIndexCells: boolean;
  disabled?: boolean;
  doRender: boolean;
  mutuallyExclusive?: boolean;
  onSelected?: Function;
  selected?: boolean;
  stickyColumn: boolean;
  toggleConfig?: FluidToggleConfig;
  toggleId?: string;
  toggleStyle?: FluidToggleType;
  unselectable?: boolean;
}

// ================================================================ //
// -- Cell Types

/**
 * Renders a default table cell, with standard data (unless truncated)
 * @param props
 * @constructor
 */
export const DefaultCell = (props: {
  header: FluidTableHeader;
  dataItem: FluidRowData;
}) => {
  const { header, dataItem } = props;
  return header.truncationOptions ? (
    <TruncatedCell header={header} dataItem={dataItem} />
  ) : (
    cellData(dataItem, header)
  );
};

/**
 * Computes a value based on the current state of the row, including
 * edits.
 * @param props
 * @example https://stackblitz.com/edit/angular-ivy-gc6zmt?file=src%2Fapp%2Fapp.component.html
 * @constructor
 */
export const ComputedCellValue = (props: {
  header: FluidTableHeader;
  dataItem: FluidRowData;
  externalState?: Record<string, unknown>;
}) => {
  const { header, dataItem, externalState } = props;
  return header.computedValueFn(dataItem, externalState);
};

/**
 * Renders a FLUID/HTML component instead of a cell value.
 * @param props
 * @constructor
 */
export const RenderedCell = (props: {
  host: Partial<FluidTable>;
  header: FluidTableHeader;
  dataItem: FluidRowData;
  componentDefinition: FluidComponentDefinition;
}) => {
  const { host, dataItem, header, componentDefinition } = props;
  const { truncationOptions } = header;
  const originalClickEvent = componentDefinition.events?.find(
    (e) => e.eventKey === 'click'
  );

  const augmentedConfig = (slot?: string) => ({
    ...componentDefinition,
    props: {
      ...componentDefinition.props,
      slot,
    },
    events: [
      ...(componentDefinition.events || []),
      {
        eventKey: 'click',
        handler: (event) => {
          event.stopPropagation();
          handleCellClick(host, dataItem, header);
          if (originalClickEvent) {
            originalClickEvent.handler(dataItem);
          }
        },
      },
    ],
  });

  return truncationOptions?.popover ? (
    <fluid-popover config={truncationOptions.popoverConfig?.(dataItem)}>
      <FluidComponentFromDefinition definition={augmentedConfig('trigger')} />
      <div
        slot={'content'}
        style={{ 'white-space': 'normal' }}
        innerHTML={
          truncationOptions.popoverTemplate?.(dataItem) ||
          componentDefinition.content
        }
      />
    </fluid-popover>
  ) : (
    <FluidComponentFromDefinition definition={augmentedConfig()} />
  );
};

/**
 * Renders a truncated cell with popover (if configured)
 * @param props
 * @constructor
 */
export const TruncatedCell = (props: {
  header: FluidTableHeader;
  dataItem: FluidRowData;
}) => {
  const { header, dataItem } = props;
  const { truncationOptions } = header;
  const { popoverConfig, popoverTemplate } = truncationOptions;
  const truncatedValue = getTruncatedValue(truncationOptions, header, dataItem);
  const popoverContent = popoverTemplate
    ? popoverTemplate(dataItem)
    : cellData(dataItem, header);

  return truncationOptions.popover ? (
    <PopoverTooltip
      displayValue={truncatedValue}
      textAlign={getCellAlignment(header)}
      popoverConfig={popoverConfig ? popoverConfig(dataItem) : undefined}
      popoverContent={popoverContent}
    />
  ) : (
    <span>{truncatedValue}</span>
  );
};

/**
 * Renders an editable cell from a form element configuration
 * @param props
 * @constructor
 */
export const EditableCell = (props) => {
  const {
    header,
    dataItem,
    addCellReference,
    handleCellUpdate,
    mutatedValue,
    disabledByCache,
    updatedRows,
    onFocus,
    disabledRow,
    theme,
    hasTableActions,
    editableCellConfig,
  } = props;

  const config = {
    theme,
    overrideGlobalTheme: !!theme,
    ...(editableCellConfig.elementType === FluidFormElementType.COMBO_BOX && {
      dynamicMenuWidth: true,
    }),
    ...editableCellConfig,
    id: `${editableCellConfig.controlName}_${dataItem[ID]}`,
    inlineField: true,
    borderless: editableCellConfig.borderless,
    updateStrategy:
      editableCellConfig.updateStrategy ||
      (hasTableActions ? FluidUpdateStrategy.INPUT : FluidUpdateStrategy.BLUR),
    initialValue: isValidValue(
      mutatedValue,
      (editableCellConfig as FluidFormElementConfig).elementType
    )
      ? mutatedValue
      : getDataAtPath(dataItem, header),
    disabled:
      (editableCellConfig.disabledIf
        ? editableCellConfig.disabledIf(dataItem)
        : false) ||
      disabledByCache ||
      disabledRow,
  };

  const formElementClasses = {
    'editable-cell': true,
    [config.elementType]: true,
    [header.cellAlignment]: !!header.cellAlignment,
  };

  return (
    <fluid-form-element
      class={formElementClasses}
      config={config}
      disabled={config.disabled}
      ref={(el) =>
        addElementReference(
          el,
          dataItem,
          header,
          mutatedValue,
          addCellReference,
          updatedRows,
          config,
          onFocus
        )
      }
      onValueChanged={(value: CustomEvent) => {
        const { cleared } = value.detail;
        handleCellUpdate(
          dataItem.rowId,
          header.dataPath,
          value.detail,
          cleared
        );
      }}
    />
  );
};

/**
 * Renders a select cell (toggle if not mutually exclusive, radio if
 * mutually exclusive)
 */

export const SelectCell = (props: TableSelectCellProps) => {
  const {
    allowTabIndexCells,
    disabled,
    doRender,
    onSelected,
    stickyColumn,
    toggleStyle,
    unselectable,
  } = props;

  if (!doRender) return;

  return (
    <td
      tabIndex={Utils.setTabIndex(allowTabIndexCells)}
      onClick={() => !disabled && !unselectable && onSelected()}
      class={{
        'table-data-cell toggle-cell': true,
        'sticky-column': !!stickyColumn,
        disabled: disabled,
      }}>
      <span
        class={{
          'data-cell-toggle-container': true,
          'toggle-switch': toggleStyle === FluidToggleType.SWITCH,
        }}>
        <RowSelection {...props} />
      </span>
    </td>
  );
};

export type ExpandPanelCellProps = {
  expanded: boolean;
  conditionalOn: boolean;
  onClick: (event: MouseEvent) => void;
  theme: FluidTheme;
  noHighlight?: boolean;
  placeholderCell?: boolean;
};

export const ExpandPanelCell = (props: ExpandPanelCellProps) => {
  const {
    expanded,
    conditionalOn,
    onClick,
    noHighlight = false,
    placeholderCell,
    theme,
  } = props;
  if (!conditionalOn) return;
  return !placeholderCell ? (
    <td
      role='button'
      aria-label='Expand Row'
      tabIndex={0}
      onClick={onClick}
      onKeyDown={(e) => EventUtils.keySelect(e)}
      class={{
        'table-data-cell expand-trigger-cell': true,
        highlight: noHighlight ? false : expanded,
      }}>
      <fluid-icon
        config={{
          key: expanded ? FluidIconKey.CHEVRON_UP : FluidIconKey.CHEVRON_DOWN,
          size: FluidSize.SMALL,
          overrideGlobalTheme: true,
          theme,
        }}
      />
    </td>
  ) : (
    <td class='table-data-cell' />
  );
};

export const RowSelection = (props: TableSelectCellProps) => {
  const { unselectable, mutuallyExclusive } = props;
  return unselectable ? (
    String.fromCharCode(160) // returns an empty space
  ) : mutuallyExclusive ? (
    <MutuallyExclusiveToggle {...props} />
  ) : (
    <NonExclusiveToggle {...props} />
  );
};

/**
 * Renders a standard, non-exclusive toggle (default checkbox unless
 * toggleType prop is supplied)
 */
export const NonExclusiveToggle = (props: TableSelectCellProps) => {
  const { disabled, toggleStyle, selected, toggleId, toggleConfig } = props;
  const toggleConfigProps: FluidToggleConfig = {
    ...toggleConfig,
    toggleType: toggleStyle,
  };
  return (
    <fluid-toggle
      class='data-cell-toggle'
      config={toggleConfigProps}
      disabled={disabled}
      controlName={toggleId}
      toggleType={toggleStyle}
      toggled={convertNonBooleanIndicatorToBoolean(selected)}
      removePadding={true}
    />
  );
};

/**
 * Renders a radio button, mutually exclusive toggle.
 */
export const MutuallyExclusiveToggle = (props: TableSelectCellProps) => {
  const { disabled, selected } = props;
  return (
    <input
      type='radio'
      disabled={disabled}
      checked={convertNonBooleanIndicatorToBoolean(selected)}
      class={{
        'data-cell-toggle-input': true,
        disabled: disabled,
      }}
    />
  );
};

/**
 * Renders a custom cell template - now deprecated in favour of a <RenderedCell />
 * @deprecated
 */
export const CellTemplate = (props) => {
  const { header, dataItem, handleClick } = props;
  const data = header.renderFullData
    ? dataItem
    : getDataAtPath(dataItem, header);
  const template: string | Node = header.cellTemplate(data);
  const isNode: boolean = typeof template === 'object';
  const setChildNode = (elementRef) => {
    if (elementRef) {
      if (elementRef?.firstChild) {
        elementRef.removeChild(elementRef.firstChild);
      }
      elementRef.appendChild(template);
    }
  };
  return (
    <div
      id={generateUniqueId()}
      style={{ margin: '0px', padding: '0px' }}
      onClick={() => handleClick()}
      ref={(element) => {
        if (isNode) {
          setChildNode(element);
        } else {
          if (element) {
            element.innerHTML = template as string;
          }
        }
      }}
    />
  );
};
